import { useCallback } from "react";
import { useSnackbar } from "../GlobalSnackbar/GlobalSnackbarContext";
import { useSSEListener } from "./SSEContext";

/**
 * Always-mounted SSE -> toast service (renders nothing).
 *
 * Lives directly under SSEProvider + GlobalSnackbarProvider so live-update
 * toasts fire on EVERY route — not only where AdminNavbar happens to be
 * mounted. These listeners used to live in AdminNavbar, which is absent on any
 * non-dashboard route, so e.g. an inbound email arriving while the user was off
 * the dashboard never toasted (the SSE connection received it but nothing was
 * subscribed to surface it).
 *
 * AdminNavbar STILL owns the badge-count listeners (job-form / referral pending
 * counts, clock state) — this component owns the TOASTS only, so there is no
 * double-toast when the navbar is also mounted.
 *
 * Gating: mounted behind an `adminIsLoggedIn` check in App.js, and the SSE
 * stream itself only delivers to admin/office connections (SSEContext) — so
 * public visitors never mount this and never receive an event regardless.
 */
const GlobalSSEToasts = () => {
  const { handleSnack, handleSuccessSnack } = useSnackbar();

  const handleCalendar = useCallback((data) => {
    // Self-origin suppression: when the originating user has 2+ tabs the server
    // lets the event through with `originConnectionId` so other same-user tabs
    // can refresh silently; the tab that made the change skips its own toast.
    const myConnId = sessionStorage.getItem("bdr_sse_connection_id");
    if (data?.originConnectionId && myConnId && data.originConnectionId === myConnId) return;
    handleSnack("Calendar was updated", 4000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* `scope: "owner"` (2026-10-09) = mail to the owner's inbox. The server
     already sends it only to people who can see it somewhere (admins, and
     anyone it was also addressed to), so this toast now names the inbox
     rather than claiming the shared one changed. */
  const handleEmail = useCallback((data) => {
    handleSnack(
      data?.scope === "owner" ? "Owner inbox updated" : "Email inbox was updated",
      4000
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleJobForm = useCallback((data) => {
    if (data?.action === "submit") {
      const name = data.clientName ? ` - ${data.clientName}` : "";
      handleSnack(`Job Review Form Ready${name}`, 4000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleReferral = useCallback((data) => {
    if (data?.action === "submit") {
      const name = data.clientName ? ` - ${data.clientName}` : "";
      handleSnack(`New Referral${name}`, 4000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGoByCompleted = useCallback((data) => {
    const client = data?.clientName || "client";
    const who = data?.refinisherName || "Refinisher";
    handleSuccessSnack(`${who} — Go By Complete for ${client}. Task Created.`, 2000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useSSEListener("calendar_update", handleCalendar);
  useSSEListener("email_update", handleEmail);
  useSSEListener("job_form_update", handleJobForm);
  useSSEListener("referral_update", handleReferral);
  useSSEListener("go_by_completed", handleGoByCompleted);

  return null;
};

export default GlobalSSEToasts;
